import React from 'react'
import { ReactReduxContext } from 'react-redux'
import { AgGridReact } from './AgGridReact'
import {
  CellClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  ColumnEventType,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowDataUpdatedEvent,
  RowEditingStartedEvent,
  RowEditingStoppedEvent,
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  RowGroupOpenedEvent,
  SelectionChangedEvent,
  Column,
  ProcessCellForExportParams,
  ColumnState,
  ExpandOrCollapseAllEvent,
  ModelUpdatedEvent,
  InitialGroupOrderComparatorParams,
  FirstDataRenderedEvent,
  SizeColumnsToContentStrategy,
  GetContextMenuItems
} from 'ag-grid-community'
import classNames from 'classnames'
import { flowRight, debounce, isNil } from 'lodash-es'
import { KeyCodes, notNilFilter } from 'utils'
import { LoaderOverlay } from '../loader/Loader'
import { ModalContext } from 'view/shared/modal'
import { DefaultPlaceholder } from './renderers'
import { EnvironmentContext } from 'view/shared/environment'
import { RowDataGetter } from 'view/shared/datagrid'
import { getRestorableColumnSettings, isCopyKeyEvent, focusCellEditor, StandardGridColumnId, tabToNextEditableCell } from './gridUtils'
import { clickEventPivotedRowCellDataMapper } from './renderers/negotiationDataUtil'
import { SendToClipboardParams } from './ClipboardProcessor'
import './styles/theme.css'
import './AgGridWrapper.css'
import { isNotEmpty, isNotNil } from 'utils/predicate'
import { ApplyTransactionGetId, createRowDataTransaction } from './createRowDataTransaction'

export interface AgGridWrapperProps<D = any> {
  dataTestId?: string
  getContextMenuItems?: GetContextMenuItems<D>
  debug?: boolean
  rowData: D[]
  applyTransactionGetId?: ApplyTransactionGetId<D>
  columnDefs: (ColGroupDef | ColDef)[]
  columnState?: readonly ColumnState[]
  gridOptions: GridOptions
  placeholder?: React.ComponentType
  className?: string
  context?: any
  showFilter?: boolean
  showGrouping?: boolean
  groupDefaultExpanded?: number
  defaultGroupSortComparator?: (groupAItems: any[], groupBItems: any[]) => number
  lockedPrimaryRowGroupColumnIds?: string[]
  editableRowGroupColumnConfig?: Record<string, RowDataGetter<any>>
  showLoader?: boolean
  canEditRow?: (rowItem: any) => boolean
  onGridReady?: (gridApi: GridApi) => void
  onFirstRowDataUpdate?: () => unknown
  onRowDataUpdate?: () => unknown
  onSelectionChanged?: (rowIds: string[]) => unknown
  onRowEditingStarted?: (rowId: string, rowItem: any) => unknown
  onRowEditingStopped?: (rowId: string, rowItem: any) => unknown
  onCellEditingStarted?: (evt: CellEditingStartedEvent) => unknown
  onCellEditingStopped?: (evt: CellEditingStoppedEvent) => unknown
  onModelUpdated?: (evt: ModelUpdatedEvent) => unknown
  onCellClicked?: (colId: string, rowIndex: number, rowItem: any, gridApi: GridApi) => unknown
  onCellValueChanged?: (rowId: string, rowItem: any, changedField: string) => unknown
  onCellValueUpdated?: (rowId: string, rowItem: any, updatedField: string, newValue: string, rawValue: string) => unknown
  onRowGroupOpened?: (expanded: boolean, all: boolean, rowItem?: any) => unknown
  onProcessCellForClipboard?: (params: ProcessCellForExportParams) => unknown
  onSendToClipboard?: (params: SendToClipboardParams) => unknown
  onColumnStateChanged?: (columnState: ColumnState[]) => unknown
  onColumnRowGroupChangeRequest?: (newColumns: Column[], prevColumns: Column[] | null) => unknown
  onSaveSelectedItemIds?: () => unknown
}

export interface AgGridWrapperState {
  gridApi: GridApi | null | undefined
  status: LoadingStatus
}

enum LoadingStatus {
  NotReady,
  Ready,
  RowsLoaded
}

export class AgGridWrapper extends React.Component<AgGridWrapperProps, AgGridWrapperState> {
  state: AgGridWrapperState = {
    gridApi: null,
    status: LoadingStatus.NotReady
  }

  private isGridReady = () => {
    const { status } = this.state
    return status !== LoadingStatus.NotReady
  }

  private prevRows: AgGridWrapperProps['rowData'] = []

  private autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: 'fitCellContents'
  }

  render() {
    const { status } = this.state
    const columnDefs = this.props.columnDefs
    const containerClassname = classNames('ag-theme-balham datagrid-container', this.props.className, {
      'datagrid-show-groupbar': this.props.showGrouping,
      'datagrid-show-filterbar': this.props.showFilter,
      'datagrid-loading': status !== LoadingStatus.RowsLoaded,
      'datagrid-loaded': status === LoadingStatus.RowsLoaded
    })

    return (
      <div className={containerClassname} data-test-id={this.props.dataTestId}>
        {(status !== LoadingStatus.RowsLoaded || this.props.showLoader) && <LoaderOverlay />}

        <ModalContext.Consumer>
          {modalContext => (
            <ReactReduxContext.Consumer>
              {reduxContext => (
                <EnvironmentContext.Consumer>
                  {environmentContext => (
                    <AgGridReact
                      context={{
                        modalContext: modalContext,
                        environmentContext: environmentContext,
                        reduxContext: reduxContext,
                        updateCellValue: this.handleUpdateCellValue,
                        changeCellValue: this.handleChangeCellValue,
                        autoSizeGroupColumn: this.autoSizeGroupColumnDebounced,
                        canEditRow: this.props.canEditRow,
                        ...this.props.context
                      }}
                      getContextMenuItems={this.props.getContextMenuItems}
                      debug={this.props.debug}
                      rowData={!this.props.applyTransactionGetId ? (this.isGridReady() ? this.props.rowData : null!) : undefined}
                      gridOptions={this.props.gridOptions}
                      columnDefs={columnDefs}
                      noRowsOverlayComponent={this.props.placeholder || DefaultPlaceholder}
                      onGridReady={this.handleGridReady}
                      enableGroupEdit={true}
                      groupDefaultExpanded={this.props.groupDefaultExpanded}
                      initialGroupOrderComparator={this.props.defaultGroupSortComparator ? this.initialGroupOrderComparator : undefined}
                      onFirstDataRendered={this.handleFirstDataRendered}
                      onRowDataUpdated={this.handleRowDataUpdated}
                      onCellKeyDown={this.handleCellKeyDown}
                      onCellClicked={this.handleCellClicked}
                      onCellValueChanged={this.handleCellValueChanged}
                      onColumnRowGroupChanged={this.handleColumnStateChanged}
                      onColumnVisible={this.handleColumnStateChanged}
                      onColumnMoved={this.handleColumnStateChanged}
                      onColumnResized={this.handleColumnStateChanged}
                      onSortChanged={this.handleColumnStateChanged}
                      onRowEditingStarted={this.handleRowEditingStarted}
                      onRowEditingStopped={this.handleRowEditingStopped}
                      tabToNextCell={tabToNextEditableCell}
                      onCellFocused={focusCellEditor}
                      onCellEditingStarted={this.handleCellEditingStarted}
                      onCellEditingStopped={this.handleCellEditingStopped}
                      onModelUpdated={this.props.onModelUpdated}
                      onSelectionChanged={this.handleSelectionChanged}
                      onRowGroupOpened={this.handleRowGroupOpened}
                      onGridColumnsChanged={this.restoreColumnStateFromStore}
                      processCellForClipboard={this.props.onProcessCellForClipboard}
                      sendToClipboard={this.props.onSendToClipboard}
                      groupAllowUnbalanced={true}
                      rowGroupPanelShow={'always'}
                      suppressGroupRowsSticky={true}
                      onExpandOrCollapseAll={this.handleExpandOrCollapseAll}
                      processUnpinnedColumns={this.handleProcessUnpinnedColumns}
                      autoSizeStrategy={this.autoSizeStrategy}
                    />
                  )}
                </EnvironmentContext.Consumer>
              )}
            </ReactReduxContext.Consumer>
          )}
        </ModalContext.Consumer>
      </div>
    )
  }

  private restoreColumnStateFromStore = ({ api }: { api: GridApi }) => {
    const { columnState } = this.props

    if (isNotNil(api) && isNotEmpty(columnState)) {
      const restorable = getRestorableColumnSettings(columnState as ColumnState[], api.getColumns() ?? [])
      api.applyColumnState({ state: restorable, applyOrder: true })
    }
  }

  componentDidUpdate(prevProps: AgGridWrapperProps) {
    const { gridApi } = this.state

    if (gridApi) {
      if (this.props.showFilter !== prevProps.showFilter) {
        gridApi.refreshHeader()
      }

      if (this.isGridReady() && prevProps.rowData !== this.props.rowData) {
        this.props.applyTransactionGetId && this.applyRowDataTransaction(gridApi, this.props.applyTransactionGetId)
      }
    }
  }

  private unmounted = false

  componentWillUnmount() {
    this.unmounted = true
  }

  private autoSizeGroupColumnDebounced = debounce(() => this.autoSizeGroupColumn(), 50)

  private autoSizeGroupColumn() {
    const { gridApi } = this.state

    // Always autosize the group column if there is row data and its grouped by something
    if (!gridApi?.isDestroyed() && gridApi && gridApi.getDisplayedRowCount() > 0 && gridApi.getRowGroupColumns().length > 0) {
      gridApi.getRowGroupColumns().some(c => c.getColId() === StandardGridColumnId.GROUPING) &&
        gridApi.autoSizeColumns([StandardGridColumnId.GROUPING])
    }
  }

  private refreshGroupRows(api: GridApi) {
    const rowNodes = api.getRenderedNodes().filter(node => node.group)
    if (rowNodes.length > 0) {
      api.refreshCells({ rowNodes })
    }
  }

  private showHideGroupColumn(gridApi: GridApi) {
    const groupingColumn = gridApi.getColumn(StandardGridColumnId.GROUPING)

    if (groupingColumn) {
      const alwaysVisible =
        groupingColumn.getColDef().headerComponentParams && groupingColumn.getColDef().headerComponentParams.alwaysVisible

      if (alwaysVisible || gridApi.getRowGroupColumns().length > 0) {
        if (!groupingColumn.isVisible()) {
          gridApi.setColumnsVisible([groupingColumn], true)
        }
      } else {
        if (groupingColumn.isVisible()) {
          gridApi.setColumnsVisible([groupingColumn], false)
        }
      }
    }
  }

  private initialGroupOrderComparator = ({ nodeA, nodeB }: InitialGroupOrderComparatorParams) => {
    const nodeAChildData =
      nodeA.group && nodeA.allLeafChildren ? nodeA.allLeafChildren.map(child => child.data).filter(notNilFilter) : [nodeA.data]
    const nodeBChildData =
      nodeB.group && nodeB.allLeafChildren ? nodeB.allLeafChildren.map(child => child.data).filter(notNilFilter) : [nodeB.data]

    return this.props.defaultGroupSortComparator!(nodeAChildData, nodeBChildData)
  }

  private handleCellKeyDown = (evt: CellKeyDownEvent) => {
    if (evt.event instanceof KeyboardEvent && evt.event.keyCode === KeyCodes.ENTER) {
      evt.api.stopEditing()
    }

    if (isCopyKeyEvent(evt)) {
      evt.api.copyToClipboard()
    }
  }

  private handleCellClicked = (evt: CellClickedEvent) => {
    const { editableRowGroupColumnConfig, canEditRow, onCellClicked } = this.props
    if (!evt.node.group && !isNil(evt.rowIndex) && onCellClicked) {
      onCellClicked(evt.column.getId(), evt.rowIndex, evt.data, evt.api)
    }

    const rowDataGetter = evt.node.rowGroupColumn && editableRowGroupColumnConfig?.[evt.node.rowGroupColumn.getColId()]

    if (evt.colDef.singleClickEdit === false || (evt.node.group && !rowDataGetter)) {
      return
    }

    if (
      !evt.column.isPinned() &&
      !isNil(evt.rowIndex) &&
      canEditRow &&
      flowRight(canEditRow, evtObj => (rowDataGetter ? rowDataGetter(evtObj) : evtObj.data), clickEventPivotedRowCellDataMapper)(evt)
    ) {
      evt.api.startEditingCell({ rowIndex: evt.rowIndex, colKey: evt.column })
    } else {
      evt.api.stopEditing()
    }
  }

  private handleGridReady = (evt: GridReadyEvent) => {
    if (this.unmounted) {
      return
    }

    this.showHideGroupColumn(evt.api)
    this.restoreColumnStateFromStore(evt)

    if (this.props.onGridReady) {
      this.props.onGridReady(evt.api)
    }

    this.setState({ gridApi: evt.api, status: LoadingStatus.Ready })

    this.props.applyTransactionGetId && this.applyRowDataTransaction(evt.api, this.props.applyTransactionGetId)
  }

  private applyRowDataTransaction(gridApi: GridApi, getId: ApplyTransactionGetId) {
    const transaction = createRowDataTransaction(getId, this.props.rowData, this.prevRows)
    gridApi.applyTransactionAsync(transaction)
    this.prevRows = this.props.rowData
  }

  private handleFirstDataRendered = (evt: FirstDataRenderedEvent) => {
    if (this.props.onFirstRowDataUpdate && evt.api.getDisplayedRowCount() > 0) {
      this.props.onFirstRowDataUpdate()
    }
  }

  private handleRowDataUpdated = (evt: RowDataUpdatedEvent) => {
    if (this.state.status === LoadingStatus.Ready) {
      this.setState({ status: LoadingStatus.RowsLoaded })
    } else {
      if (evt.api.getSelectedNodes().length > 0) {
        evt.api.refreshHeader()
      }

      this.refreshGroupRows(evt.api)
      this.props.onRowDataUpdate?.()
    }

    if (this.props.onFirstRowDataUpdate && evt.api.getDisplayedRowCount() > 0) {
      this.props.onFirstRowDataUpdate()
    }
  }

  private handleSelectionChanged = (evt: SelectionChangedEvent) => {
    /* Refresh the header to update any action buttons there */
    evt.api.refreshHeader()
    this.refreshGroupRows(evt.api)

    //Calls the function to save the selected Item Ids to the Store
    if (this.props.onSaveSelectedItemIds) {
      this.props.onSaveSelectedItemIds()
    }

    // Focus on first displayed cell to ensure grid body gets focus prior to a ctrl+c
    const selectedNodes = evt.api.getSelectedNodes()
    if (selectedNodes.length > 0 && selectedNodes.length === evt.api.getDisplayedRowCount() && !isNil(selectedNodes[0].rowIndex)) {
      evt.api.setFocusedCell(selectedNodes[0].rowIndex, evt.api.getAllDisplayedColumns()[0])
    }

    if (this.props.onSelectionChanged) {
      const selectedRowIds = evt.api
        .getSelectedNodes()
        .map(node => node.id)
        .filter(notNilFilter)
      this.props.onSelectionChanged(selectedRowIds)
    }
  }

  private handleExpandOrCollapseAll = (evt: ExpandOrCollapseAllEvent) => {
    if (this.props.onRowGroupOpened) {
      switch (evt.source) {
        case 'expandAll':
          this.props.onRowGroupOpened(true, true)
          break
        case 'collapseAll':
          this.props.onRowGroupOpened(false, true)
          break
        default:
          return
      }
    }
  }

  private handleRowGroupOpened = (evt: RowGroupOpenedEvent) => {
    this.refreshGroupRows(evt.api)

    if (this.props.onRowGroupOpened) {
      this.props.onRowGroupOpened(evt.node.expanded, false, evt.node.data)
    }
  }

  private handleRowEditingStarted = (evt: RowEditingStartedEvent) => {
    if (this.props.onRowEditingStarted && !isNil(evt.node.id)) {
      this.props.onRowEditingStarted(evt.node.id, evt.node.data)
    }
  }

  private handleRowEditingStopped = (evt: RowEditingStoppedEvent) => {
    if (this.props.onRowEditingStopped && !isNil(evt.node.id)) {
      this.props.onRowEditingStopped(evt.node.id, evt.node.data)
    }
  }

  private handleCellEditingStarted = (evt: CellEditingStartedEvent) => {
    if (this.props.onCellEditingStarted) {
      this.props.onCellEditingStarted(evt)
    }
  }

  private handleCellEditingStopped = (evt: CellEditingStoppedEvent) => {
    if (this.props.onCellEditingStopped) {
      this.props.onCellEditingStopped(evt)
    }
  }

  private handleColumnStateChanged = (evt?: { source: ColumnEventType | string }) => {
    // If its an autosize event, we don't need to persist the column settings
    if (evt?.source === 'autosizeColumns') {
      return
    }

    const { gridApi } = this.state

    if (gridApi) {
      this.showHideGroupColumn(gridApi)
      if (this.props.onColumnStateChanged) {
        this.props.onColumnStateChanged(gridApi.getColumnState())
      }
    }
  }

  private handleChangeCellValue = (rowId: string, rowItem: any, field: string) => {
    if (this.props.onCellValueChanged) {
      this.props.onCellValueChanged(rowId, rowItem, field)
    }
  }

  private handleUpdateCellValue = (rowId: string, rowItem: any, field: string, newValue: any, rawValue?: any) => {
    if (this.props.onCellValueUpdated) {
      this.props.onCellValueUpdated(rowId, rowItem, field, newValue, rawValue)
    }
  }

  private handleCellValueChanged = (_evt: CellValueChangedEvent) => {
    /* This should never happen as it means ag-grid is about to mutate our data */
    throw new Error('Unexpected cell update received')
  }

  private handleProcessUnpinnedColumns = () => {
    // This returns a empty array to make sure any pinned columns are not moved out of being pinned
    return []
  }
}
